import React, { useState } from "react";
import Layout from "@/components/utu/Layout";
import { GlassCard, OutlinePillButton } from "@/components/utu/atoms";
import { SEO } from "@/components/SEO";
import { INSIGHTS_ARTICLES } from "@/mock";
import { BookOpen, Calendar, User, Clock, ArrowRight, X } from "lucide-react";

export default function Insights() {
  const [selectedArticle, setSelectedArticle] = useState(null);

  return (
    <Layout>
      <SEO
        title="Research Insights & Articles"
        description="Read the latest research papers, technical articles, and engineering insights from the UTU Africa AI lab. Topics include MCP architectures, low-resource tokenizers, and private SLM scaling."
        url="/insights"
      />
      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Header Hero */}
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-[hsl(var(--utu-gold))] backdrop-blur-xl mb-4">
            <BookOpen className="h-4 w-4" />
            Insights & Papers
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl mb-6">
            Linguistic & AI Research
          </h1>
          <p className="text-lg text-white/70 leading-relaxed">
            Technical breakdowns, dataset releases, and architectural analysis from our engineers and research collaborators.
          </p>
        </div>

        {/* Article Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-16">
          {INSIGHTS_ARTICLES.map((art) => (
            <GlassCard key={art.id} className="p-6 flex flex-col justify-between h-full border border-white/10 hover:border-amber-500/30 transition-all duration-300">
              <div>
                <div className="flex flex-wrap items-center gap-3 text-[10px] text-white/50 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {art.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {art.readTime}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">{art.title}</h3>
                <p className="text-xs text-white/60 leading-relaxed mb-6 line-clamp-3">{art.summary}</p>
              </div>

              <div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                  <span className="text-[10px] text-white/50 font-semibold">{art.author}</span>
                  <button
                    onClick={() => setSelectedArticle(art)}
                    className="text-xs text-[hsl(var(--utu-gold))] font-semibold flex items-center gap-1 hover:underline"
                  >
                    Read Paper
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* RSS Feed Notice */}
        <GlassCard className="p-6 md:p-8 text-center border-dashed border-white/10 max-w-2xl mx-auto">
          <h4 className="text-lg font-bold text-white mb-2">Automated Research Pipeline</h4>
          <p className="text-xs text-white/60 leading-relaxed">
            Our insights feed is automatically aggregated from our internal Git commits and researcher notebooks via our article publish pipeline. Subscribe to our RSS feed to receive updates directly.
          </p>
        </GlassCard>

        {/* Article Reader Modal */}
        {selectedArticle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
            <GlassCard className="w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6 md:p-10 border border-white/10 shadow-2xl relative">
              <button
                onClick={() => setSelectedArticle(null)}
                className="absolute right-4 top-4 p-2 text-white/70 hover:text-white rounded-full bg-white/5 border border-white/10"
                aria-label="Close reader"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="space-y-6">
                <div>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-white/50 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-[hsl(var(--utu-gold))]" />
                      {selectedArticle.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-[hsl(var(--utu-gold))]" />
                      {selectedArticle.readTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="h-3.5 w-3.5 text-[hsl(var(--utu-gold))]" />
                      {selectedArticle.author}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">{selectedArticle.title}</h2>
                </div>

                <div className="h-px bg-white/10" />

                <div className="text-sm md:text-base text-white/80 leading-relaxed space-y-4 font-normal whitespace-pre-line">
                  {selectedArticle.content}
                </div>

                <div className="pt-6 border-t border-white/10 flex justify-end">
                  <OutlinePillButton onClick={() => setSelectedArticle(null)} className="h-9 px-4 text-xs">
                    Close Document
                  </OutlinePillButton>
                </div>
              </div>
            </GlassCard>
          </div>
        )}
      </section>
    </Layout>
  );
}
